/**
   * @memberof SMART2
   * @ngdoc directive
   * @name Radio
   * @description This directive is useful for creating a radio button or a group of radio buttons.
   * 
   * @attr {String} label
   *    Label to be displayed for identification of this ui element
   * @attr {Array} options
   *    Number of radio buttons to be created. Only one option can be selected at a time.
   * @attr {Object} ng-model
   *    Default selected option. This is expected to be one of the 'options'.
   * @attr {Object} ng-model-options
   *    https://docs.angularjs.org/api/ng/directive/ngModelOptions. 
   *    <a href="SMART2.Textfield.html">Click here</a> and scoll down to 'Textfield with ng-model-options' to see example reference.
   * @attr {String} datakey
   *    A property of an option to be used to display the label for option. Default value is 'title'.
   * @attr {String} layout
   *    'vertical' or 'horizontal' layout
   * @attr {Boolean} validate
   *    If set to true, this ui element will be validated on the basis of rules passed to it
   * @attr {Array} rules 
   *    Rules to be evaluated when this element's blur event is fired. A rule must have 'rule' and 'errorMessage' properties (keys).
   *    'rule' must be a condition or group of conditions. 'errorMessage' will be the message to be displayed when corresponding rule fails. See example for more.
   * @attr {String} error-message 
   *    Error message to be displayed. This attribute can be set at any point to display an error message.
   * @attr {Boolean} is-mandatory 
   *    If set to true, default error message will be displayed when blur event is fired and this ui element is left blank
   * @attr {Boolean} is-visible 
   *    If set to true, this ui element will be displayed on form regardless of 'is-mandatory' property's value. 
   *    This ui element can be removed from form by clicking 'x' button on top right corner of this ui element.
   * @attr {Number} colspan
   *    Number of columns to be occupied by this ui element. 1 column is equal to 1 ui element.
   *    Default value is 1.
   *    For e.g. If colspan is set to 2, this ui element will occupy width of 2 ui elements (fields).
   * @attr {Function} on-change
   *    Callback function when any of the buttons from radio group is selected
   * @type cool
   * 
   * @example
   Dynamic:
   Controller:
       $scope.config = {
            "modelData": {
                "currency": { "code": "€", "name": "EUR" }
            }, 
            "formConfig": {
                "sections": [
                    {
                        "isMandatory": true,
                        "rows": [
                            {
                                "properties": [
                                    {
                                        "label": "Currency",
                                        "type": "radio",
                                        "isMandatory": true,
                                        "data": "currency",
                                        "colspan": 1,
                                        "onChange": "onChange",  //  controller function
                                        "attributes": {
                                            "options": [{
                                                "code": "$",
                                                "name": "USD"
                                            }, {
                                                "code": "€",
                                                "name": "EUR"
                                            }],
                                            "datakey": "name"
                                        },
                                        "rules": [
                                            { 
                                                "rule": "this.title == \"Option 2\"", 
                                                "error": "Invalid selection" 
                                            }
                                        ]
                                    }
                                ]    
                            }
                        ]
                    }
                ]
            }
        };
    Usage:
        <smart-form-widget form-config="config.formConfig" model-data="config.modelData"></smart-form-widget>
   
   * @example
   Static:
   Controller:
       $scope.currencyOptions = [{
            "code": "$",
            "name": "USD"
       }, {
           "code": "€",
           "name": "EUR"
       }];
       $scope.selectedCurrency = { "code": "€", "name": "EUR" };
       $scope.onChange = function(selectedCurrency) {
           console.log(selectedCurrency);
       };
   Usage:
       <smart-radio label="Currency" ng-model="selectedCurrency" options="currencyOptions" datakey="name" on-change="onChange(selectedCurrency)"></smart-radio> 

   * @example
   Radio with disabled option:
   Controller:
       $scope.currencyOptions = [{
            "code": "$",
            "name": "USD",
            "disable": true
       }, {
           "code": "€",
           "name": "EUR"
       }];
       $scope.selectedCurrency = { "code": "€", "name": "EUR" };
   Usage:
       <smart-radio label="Currency" ng-model="selectedCurrency" options="currencyOptions" datakey="name"></smart-radio> 
   */

(function () {
    'use strict';
    var radioGroupId = 0;

    angular.module('SMART2').directive('smartRadio', ['$timeout', '$translate', 'ScrollTo', 'RuleEngine', function ($timeout, $translate, ScrollTo, RuleEngine) {
        return {
            restrict: 'E',
            replace: true,
            require: '?ngModel',
            scope: {
                label: '@',
                options: '=?',
                ngModelCopy: '=?ngModel',
                isMandatory: '@',
                isVisible: '=?',
                layout: '@',
                rules: '@',
                validate: '=?',
                focus: '=?',
                parentElement: '@',
                errorMessage: '@',
                onChange: '&',
                removable: '@',
                datakey: '@',
                optionId: '@',
                groupId: '@'
            },
            link: function (scope, element, attrs, ngModelCtrl) {
                scope.isRemovable = scope.$eval(scope.removable) == undefined ? true : scope.$eval(scope.removable);

                var isNgModelFunction = typeof scope.ngModelCopy == 'function';

                // key to be used to bind options
                scope.dataKey = scope.datakey ? scope.datakey : 'title';

                //uniqueID
                radioGroupId = radioGroupId + 1;
                scope.radioGrouptId = (attrs.groupId) ? attrs.groupId : "radio-group-" + radioGroupId;

                if (typeof attrs.optionId != "undefined") {
                    scope.optionId = attrs.optionId;
                }
                 //  following line of code has been added for pendo
                var uniqueIDGenerator = function () {
                    var d = new Date().getTime();
                    var uniqueID = 'xx2xxpxxoxx'.replace(/[xy]/g, function (c) {
                        var r = (d + Math.random() * 16) % 16 | 0;
                        d = Math.floor(d / 16);
                        return (c == 'x' ? r : (r & 0x3 | 0x8)).toString(16);
                    });
                    return uniqueID;
                };
                var radioUniqueId = uniqueIDGenerator();    
                var tmpId = '';
                if(attrs.id){
                    tmpId = "radio_" + attrs.id;        
                    var tmpElementCount = $('#' + tmpId).length;
                    if(tmpElementCount > 0) {
                        tmpId  = tmpId + '_' + radioUniqueId;
                    }                  
                }
                else {                   
                    tmpId = "radio_" + radioUniqueId;                            
                }
                element.attr("id", tmpId);
                //check is gap
                scope.isGap = (attrs.gap) ? scope.$eval(attrs.gap) : false;

                scope.layout = scope.layout ? scope.layout : 'horizontal';


                /*
                 *  Following code has been added to make ng-model-options work
                 *  ngModelCtrl.$render & ngModelCtrl.$setViewValue serve the purpose
                 */
                if (isNgModelFunction) {
                    try {
                        ngModelCtrl.$render = function () {
                            scope.ngModel = ngModelCtrl.$modelValue;
                            scope.selectedOption = scope.ngModel;
                        };
                    }
                    catch (e) { }
                }

                var updateView = function () {
                    if (isNgModelFunction) {
                        try {
                            ngModelCtrl.$setViewValue(scope.ngModel);
                        }
                        catch (e) { }
                    }
                };


                /*
                 * Focus field when focus is set to true
                 */
                var onFocus = scope.$watch('focus', function (newVal) {
                    if (newVal) {
                        ScrollTo.perform(element, angular.element('#' + scope.parentElement));
                        scope.focus = false;
                    }
                });

                /*
                 * Validate field whene validate is set to true
                 */
                var onValidate = scope.$watch('validate', function (newValue) {
                    if (newValue != undefined && newValue) {
                        scope.validateRules();
                    }
                });

                /*
                 * Watch needs to be added on ngModel
                 */
                var onNgModel = scope.$watch(isNgModelFunction ? 'ngModel' : 'ngModelCopy', function (newVal) {
                    scope.selectedOption = newVal;
                }, true);

                /*
                 * Validate rules
                 */
                scope.validateRules = function () {
                    if (scope.$eval(scope.isMandatory) && (isNgModelFunction && (scope.ngModel === '' || scope.ngModel === undefined || scope.ngModel === null)) || (!isNgModelFunction && (scope.ngModelCopy === '' || scope.ngModelCopy === undefined || scope.ngModelCopy === null))) {
                        scope.validate = true;
                        scope.errorMessage = $translate.instant('selectOptionErrMsg');
                        return;
                    }

                    scope.validate = false;
                    scope.errorMessage = null;

                    if (scope.rules) {
                        var rules = scope.$eval(scope.rules);
                        var isFoundInvalid = false,
                            ruleLen = rules.length;

                        for (var i = 0; i < ruleLen; i++) {
                            var ruleT = rules[i]; // temp hold
                            if (typeof ruleT == 'object') {
                                if (eval((ruleT.rule).replace(/this/g, 'scope.selectedOption'))) {
                                    scope.validate = true;
                                    scope.errorMessage = ruleT.error;
                                    break;
                                }
                                else {
                                    if (!isFoundInvalid) {
                                        RuleEngine.isValid(ruleT, function (e) {
                                            scope.validate = true;
                                            scope.errorMessage = e.errorData.error;
                                            isFoundInvalid = true;
                                        });
                                    }
                                    else {
                                        break;
                                    }
                                }
                            }
                        }
                    }
                };

                scope.ngChange = function (selectedOption) {
                    // Commented below code for fixing TWO-2748
                    //if (!scope.$eval(scope.isMandatory) && angular.isDefined(scope.selectedOption) && scope.selectedOption[scope.dataKey] == selectedOption[scope.dataKey]) {
                    //    scope.selectedOption = '';
                    //}
                    //else {
                    //    scope.selectedOption = selectedOption;
                    //}

                    // Added below code for fixing TWO-2748
                    scope.selectedOption = selectedOption;

                    if (!scope.groupId) {
                        if (isNgModelFunction) {
                            scope.ngModel = scope.selectedOption;
                        }
                        else {
                            scope.ngModelCopy = scope.selectedOption;
                        }
                    }
                    updateView();
                    scope.validateRules();
                    var toutPromise = $timeout(function () {
                        scope.onChange();
                        $timeout.cancel(toutPromise); // cancel the timeout promise to avoid any leakage.
                    });
                };

                scope.$on('$destroy', function () {
                    onFocus();
                    onValidate();
                    onNgModel();
                    element.remove();
                    scope = null;
                });
            },
            templateUrl: 'shared/directives/uiElements/smartRadio/smartRadioTemplate.html'
        };
    }]);
})();